import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faCog, faEnvelopeOpen, faSearch, faSignOutAlt, faUserShield } from "@fortawesome/free-solid-svg-icons";
import { faUserCircle } from "@fortawesome/free-regular-svg-icons";
import { Row, Col, Nav, Form, Image, Navbar, Dropdown, Container, ListGroup, InputGroup, Button, ButtonGroup } from '@themesberg/react-bootstrap';
import { UserContext } from "../utils/UserContext";
import { AuthContext } from "../utils/AuthContext";  // ✅ use AuthContext
import API from "../utils/api";
import i18n from "../i18n";
export default function Topbar() {
  const { profile, setProfile } = useContext(UserContext);

  // const { logout } = useContext(AuthContext);   // ✅ grab logout from AuthContext
  const [notifications, setNotifications] = useState([]);
  const [areNotificationsRead, setAreNotificationsRead] = useState(true);
  const backendURL = `https://api-dashboard.nordmann-inkasso.de
`;
  const navigate = useNavigate();
  // const role = localStorage.getItem("role");
  const [lang, setLang] = useState(i18n.language);

  // sync with i18next events
  useEffect(() => {
    const handleLangChange = (lng) => setLang(lng);
    i18n.on("languageChanged", handleLangChange);
    return () => i18n.off("languageChanged", handleLangChange);
  }, [i18n]);
  const profileImageUrl = profile?.profilePicture && profile.profilePicture.trim() !== ""
    ? `${backendURL}/${profile.profilePicture.replace(/\\/g, "/")}`
    : null;


  const { endImpersonation, logout, role, user } = useContext(AuthContext);
  const isSubUser = !!user.parentUser; // true if parentUser exists
  const handleLogout = async () => {


    if (localStorage.getItem("adminToken")) {
      // ✅ End impersonation using context
      const restored = await endImpersonation();

      if (restored) {
        navigate("/admin/dashboard-overview"); // back to admin panel
      } else {
        navigate("/"); // fallback
      }
    } else {
      // ✅ Normal logout
      await logout();
      navigate("/");
    }
  };

  // Fetch profile
  useEffect(() => {
    async function fetchProfile() {
      try {
        const res = await API.get("/profile/me");

        if (res.data) {
          setProfile(res.data);
        } else {
          if (role === "admin") {
            setProfile({ fullName: "Admin", profilePicture: "A" });
          } else {
            setProfile(null);
          }
        }
      } catch {
        if (role === "admin") {
          setProfile({ firstName: "Admin", lastName: "", profilePicture: "" });
        } else {
          setProfile(null);
        }
      }
    }
    fetchProfile();
  }, []);


  // Fetch notifications when profile changes

  useEffect(() => {
    if (!profile) return;

    async function fetchNotifications() {
      try {
        const res = await API.get("claims/notifications");

        if (res.data && res.data.data) {
          setNotifications(res.data.data);
          const anyUnread = res.data.data.some(n => !n.read);
          setAreNotificationsRead(!anyUnread);
        }
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    }

    // Fetch immediately on mount
    fetchNotifications();

    // Set interval to fetch every 10 seconds (10000 ms)
    const intervalId = setInterval(() => {
      fetchNotifications();
    }, 50000000);

    // Cleanup interval on unmount or profile change
    return () => clearInterval(intervalId);

  }, [profile]);


  // Mark all notifications read (for dropdown toggle)
  const markNotificationsAsRead = () => {
    setAreNotificationsRead(true);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    // You might want to call API here to mark all as read on server
  };

  // Toggle read/unread for single notification
  const toggleReadStatus = async (id, currentStatus) => {
    try {
      // Call your API to update notification read status
      await API.patch(`claims/notifications/${id}`, { read: !currentStatus });

      // Update locally
      setNotifications(prev =>
        prev.map(n =>
          n._id === id ? { ...n, read: !currentStatus } : n
        )
      );

      // Update unread badge
      const anyUnread = notifications.some(n => n._id !== id ? !n.read : currentStatus);
      setAreNotificationsRead(!anyUnread);

    } catch (error) {
      console.error("Failed to update notification read status", error);
    }
  };

  // Format date/time: "MMM dd" and "HH:mm" (24h without seconds)
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const dateOptions = { month: 'short', day: 'numeric' };
    const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: false };
    const formattedDate = date.toLocaleDateString(undefined, dateOptions);
    const formattedTime = date.toLocaleTimeString(undefined, timeOptions);
    return `${formattedDate} ${formattedTime}`;
  };

  // Generate custom message based on notification data
const getNotificationMessage = ({ claimName, status, message }) => {
  const statusLower = (status || '').toLowerCase();

  if (statusLower === "approved") {
    return (
      <>
        Ihr Antrag <strong>{claimName}</strong> wurde genehmigt. Sie können nun das Mandat herunterladen und einreichen.
      </>
    );
  } else if (statusLower === "waiting for admin action") {
    return (
      <>
        Die Abrechnung für Ihren Antrag <strong>{claimName}</strong> wurde eingereicht und wartet auf Genehmigung.
      </>
    );
  } else if (statusLower === "rejected") {
    return (
      <>
        Ihr Antrag <strong>{claimName}</strong> wurde abgelehnt. Bitte überprüfen Sie den Antrag für weitere Details.
      </>
    );
  } else if (statusLower === "waiting") {
    return (
      <>
        Das Mandat für Ihren Antrag <strong>{claimName}</strong> wurde eingereicht. Sie werden benachrichtigt, sobald die Arbeit beginnt.
      </>
    );
  } else if (statusLower === "in progress") {
    return (
      <>
        Die Bearbeitung Ihres Antrags <strong>{claimName}</strong> hat begonnen. Sie werden nach Abschluss benachrichtigt.
      </>
    );
  } else if (statusLower === "completed") {
    return (
      <>
        Glückwunsch! Ihr Antrag <strong>{claimName}</strong> wurde erfolgreich abgeschlossen.
      </>
    );
  }

  return message || "Sie haben eine neue Benachrichtigung.";
};





  const unreadCount = notifications.filter(n => !n.read).length;

  const Notification = ({ _id, sender, time, message, read = false, claimName, status }) => {
    const readClassName = read ? "" : "text-danger";
    return (
      <ListGroup.Item className="border-bottom border-light">
        <Row className="align-items-center">
          <Col className="col-auto">
            {profileImageUrl ? (
              <Image
                src={profileImageUrl}
                className="user-avatar md-avatar rounded-circle"
                style={{ objectFit: "cover" }}
              />
            ) : (
              <div
                className="user-avatar md-avatar rounded-circle d-flex align-items-center justify-content-center bg-primary text-white"
                style={{ width: "40px", height: "40px", fontWeight: "bold", fontSize: "18px" }}
              >
                {profile?.firstName?.charAt(0)?.toUpperCase()}
              </div>
            )}
          </Col>

          <Col className="ps-0 ms--2">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="h6 mb-0 text-small">{sender}</h4>
              </div>
              <div className="text-end">
                <small className={readClassName}>{formatDateTime(time)}</small>
              </div>
            </div>
            <p className="font-small mt-1 mb-0">{getNotificationMessage({ claimName, status, message })}</p>
            <Button
              size="sm"
              variant={read ? "outline-secondary" : "primary"}
              onClick={() => toggleReadStatus(_id, read)}
            >
              Mark as {read ? "Unread" : "Read"}
            </Button>
          </Col>
        </Row>
      </ListGroup.Item>
    );
  };

  if (!profile && role !== "admin") {
    return (
      <Navbar variant="dark" expanded className="ps-0 pe-2 pb-0">
        <Container fluid className="px-0">

          <div className="d-flex justify-content-end w-100 mb-4">
            <div className="text-center ">
              <button
                onClick={() => i18n.changeLanguage("en")}
                className={`lang-btn ${lang.startsWith("en") ? "active" : ""}`}
              >
                EN
              </button>

              <button
                onClick={() => i18n.changeLanguage("de")}
                className={`lang-btn ${lang.startsWith("de") ? "active" : ""}`}
              >
                DE
              </button>
            </div>
            <Button onClick={() => navigate("/client/your-profile")} variant="primary">Create Profile</Button>
          </div>
        </Container>
      </Navbar>
    );
  }


  return (
    <Navbar variant="dark" expanded className="ps-0 pe-2 mb-4 pb-0">
      <Container fluid className="px-0">
        <div className="d-flex justify-content-end w-100">


          <Nav className="align-items-center">
            {/* Notifications Dropdown */}
            <div className="text-center ">
              <button
                onClick={() => i18n.changeLanguage("en")}
                className={`lang-btn ${lang.startsWith("en") ? "active" : ""}`}
              >
                EN
              </button>

              <button
                onClick={() => i18n.changeLanguage("de")}
                className={`lang-btn ${lang.startsWith("de") ? "active" : ""}`}
              >
                DE
              </button>
            </div>



            <Dropdown as={Nav.Item} >
              <Dropdown.Toggle as={Nav.Link} className="text-dark icon-notifications me-lg-3" style={{ position: "relative" }}>
                <span className="icon icon-sm">
                  <FontAwesomeIcon icon={faBell} className="bell-shake" />
                  {unreadCount > 0 && (
                    <span
                      className="icon-badge rounded-circle unread-notifications"
                      style={{
                        position: "absolute",
                        top: "-13px",
                        right: "11px",
                        backgroundColor: "red",
                        color: "white",
                        fontSize: "0.7rem",
                        width: "16px",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bolder"
                      }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu className="dashboard-dropdown notifications-dropdown dropdown-menu-lg dropdown-menu-center mt-2 py-0">
                <ListGroup style={{ maxHeight: '500px', overflowY: 'auto' }} className="list-group-flush">
                  <Nav.Link
                    href="#"
                    className="text-center text-primary fw-bold border-bottom border-light py-3"
                    style={{
                      position: 'sticky',
                      top: 0,
                      backgroundColor: 'white', // or your dropdown bg color
                      zIndex: 10,               // higher than the list items
                    }}
                  >
                    Notifications
                  </Nav.Link>

                  {notifications.length === 0 ? (
                    <ListGroup.Item className="text-center">No notifications</ListGroup.Item>
                  ) : (
                    notifications.map(n => (
                      <Notification
                        key={`notification-${n._id}`}
                        _id={n._id}
                        sender="System"
                        time={n.createdAt}
                        message={n.message}
                        read={n.read}
                        claimName={n.claim?.name}    // access nested claim name
                        status={n.status}
                      // access nested claim status
                      />
                    ))

                  )}

                  {/* <Dropdown.Item className="text-center text-primary fw-bold py-3">
                    View all
                  </Dropdown.Item> */}
                </ListGroup>
              </Dropdown.Menu>
            </Dropdown>

            {/* Profile Dropdown */}
            <Dropdown as={Nav.Item}>
              <Dropdown.Toggle as={Nav.Link} className="pt-1 px-0">
                <div className="media d-flex align-items-center">
                  {profileImageUrl ? (
                    <Image src={profileImageUrl} className="user-avatar md-avatar rounded-circle" style={{ objectFit: "cover" }} />
                  ) : (
                    <div className="user-avatar md-avatar rounded-circle d-flex align-items-center justify-content-center bg-primary text-white"
                      style={{ width: "40px", height: "40px", fontWeight: "bold", fontSize: "18px" }}>
                      {profile?.fullName?.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                  <div className="media-body ms-2 text-dark align-items-center d-none d-lg-block">
                    <span className="mb-0 font-small fw-bold">{profile?.firstName} {profile?.lastName}</span>
                  </div>
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="user-dropdown dropdown-menu-right mt-2">
                {(role === "client" || role === "specialClient") && (!isSubUser) && (
                  <Dropdown.Item
                    className="fw-bold"
                    onClick={() => navigate("/client/your-profile")}
                  >
                    <FontAwesomeIcon icon={faUserCircle} className="me-2" /> My Profile
                  </Dropdown.Item>
                )}

                <Dropdown.Divider />
                <Dropdown.Item className="fw-bold" onClick={handleLogout} style={{ cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faSignOutAlt} className="text-danger me-2" /> Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </div>
      </Container>
    </Navbar>
  );
}
